/**
 * components/charts/Funnel — the decision funnel: candidates screened → full committee
 * ran → ENTER. A funnel is a magnitude that only ever narrows, so it's one sequential
 * hue (the accent) with each stage's bar width proportional to its count against the
 * widest (top) stage; the step-down between stages is the drop-off. Each row shows its
 * absolute count and its share of the top of the funnel, and a muted conversion delta
 * from the previous stage — so the reader sees both "how many" and "how leaky".
 */
import { pctPlain } from '../../lib/format';
import type { FunnelStage } from '../../lib/analytics';

interface FunnelProps {
  readonly stages: readonly FunnelStage[];
}

export function Funnel({ stages }: FunnelProps): JSX.Element {
  const top = stages[0]?.count ?? 0;
  if (stages.length === 0 || top === 0) {
    return <div className="state"><span className="muted">NO DATA YET</span></div>;
  }

  return (
    <div className="funnel" role="img" aria-label="Decision funnel from candidates to ENTER">
      {stages.map((s, i) => {
        const frac = top > 0 ? s.count / top : 0;
        const prev = i > 0 ? stages[i - 1]!.count : null;
        const conv = prev && prev > 0 ? s.count / prev : null;
        return (
          <div className="funnel__row" key={s.key}>
            <div className="funnel__head">
              <span className="funnel__label">{s.label}</span>
              <span className="funnel__count num">
                {s.count} <span className="muted">{pctPlain(frac, 0)}</span>
              </span>
            </div>
            <div className="funnel__track">
              <span
                className="funnel__bar"
                style={{ width: `${Math.max(2, frac * 100)}%` }}
                title={`${s.label}: ${s.count} (${pctPlain(frac, 0)} of candidates)`}
              />
            </div>
            {conv != null && (
              <span className="funnel__conv muted">↳ {pctPlain(conv, 0)} from “{stages[i - 1]!.label}”</span>
            )}
          </div>
        );
      })}
    </div>
  );
}
